import {
	Agreement,
	AgreementType,
	CalculationMethod,
	CargoLinkedCharter,
} from "types/index";
import { AgreementInput, CharterType } from "__generated__/graphql";
import {
	convertCalculationMethodFormToInput,
	convertCalculationMethodToForm,
} from "view/calculationmethods/utils/CalculationMethodUtils";
import {
	convertConditionFormToInput,
	convertConditionToForm,
} from "view/agreementconditions/utils/AgreementConditionUtils";
import { AgreementSchema } from "view/agreements/schema";
import { AgreementTypeEnum, CharterSubtype, FavoriteType } from "__generated__/enums";
import { Favorite } from "types/commons";
import { Box, Tooltip, Typography } from "@mui/material";
import { intl } from "LocaleWrapper";
import { InfoOutlined } from "@mui/icons-material";
import { AgreementFormInput, AgreementFormOutput } from "view/agreements/validation/agreement.zod";
import { CardAction } from "@cofano/cck/Card/Card";
import PjkRateAction from "view/agreements/components/PjkRateAction";

export const convertAgreementToForm = (agreement: Agreement) => {
	const schemaForm = AgreementSchema[agreement.type]?.toForm?.(agreement);

	const baseForm: AgreementFormInput = {
		id: agreement.id,
		uuid: crypto.randomUUID(),
		type: agreement.type,
		description: agreement.description ?? undefined,
		manualActionRequired: agreement.manualActionRequired ?? false,
		summary: agreement.summary ?? undefined,
		conditionsSummary: agreement.conditionsSummary ?? undefined,
		priority: agreement.priority,
		costType: agreement.costType ?? undefined,
		calculationMethod: agreement.calculationMethod ? convertCalculationMethodToForm(agreement.calculationMethod, agreement.type) : null,
		conditions: agreement.conditions?.map((x) => convertConditionToForm(x)) ?? [],
		isDefault: agreement.isDefault,
		invoiceTo: agreement.invoiceTo ?? undefined,
		invoiceGroupType: agreement.invoiceGroupType ?? undefined,
	};

	return schemaForm ? { ...baseForm, ...schemaForm } : { ...baseForm };
};

export const stripTemplateIds = (agreement: ReturnType<typeof convertAgreementToForm>) => ({
	...agreement,
	id: undefined,
	conditions: agreement.conditions?.map((condition) => ({ ...condition, id: undefined })),
	calculationMethod: agreement.calculationMethod
		? { ...agreement.calculationMethod, id: undefined }
		: null,
});


export const convertAgreementFormToInput = (form: AgreementFormOutput, charterType: CharterType, charterSubtype: CharterSubtype) => {
	const schemaInput = form.type ? AgreementSchema[form.type]?.toInput?.(form) : undefined;

	const baseInput: Partial<AgreementInput> = {
		id: form.id,
		uuid: form.uuid,
		type: form.type,
		charterType: charterType,
		charterSubtype: charterSubtype,
		priority: form.priority ?? undefined,
		description: form.description,
		manualActionRequired: form.manualActionRequired ?? false,
		primary: form.primary,
		costType: form.costType?.id ?? undefined,
		calculationMethod: form.calculationMethod ? convertCalculationMethodFormToInput(form.calculationMethod, form.type) : undefined,
		conditions: form.conditions?.map((x) => convertConditionFormToInput(x)),
		invoiceTo: form.invoiceTo?.id ?? undefined,
		invoiceGroupType: form.invoiceGroupType?.id ?? undefined,
	};

	return schemaInput ? { ...baseInput, ...schemaInput } : { ...baseInput };
};

type SortableAgreement = {
	id?: number | null;
	uuid?: string | null;
	type?: AgreementTypeEnum | null;
	priority?: number | null;
	summary?: string | null;
	conditionsSummary?: string | null;
}

const getCustomAgreementTypes = (customAgreementTypes?: AgreementTypeEnum[]) => {
	return customAgreementTypes && customAgreementTypes.length > 0 ? customAgreementTypes : undefined;
};

const getFavoriteAgreementTypes = (favorites: Favorite[], agreementTypes: AgreementType[]) => {
	return favorites.filter(f => f.entityType === FavoriteType.AgreementType)
		.map(f => agreementTypes.find(t => t.id === f.entityId)?.type)
		.flatMap(t => (t ? [ t ] : []));
};

const getAgreementTypeOrder = (agreementTypes: AgreementType[]) => {
	const orderMap = new Map<AgreementTypeEnum, number>();
	for (const agreementType of agreementTypes ?? []) {
		orderMap.set(agreementType.type, agreementType.agreementTypeOrder ?? Number.POSITIVE_INFINITY);
	}
	return orderMap;
};

export const getDefaultInvoiceGroupType = (agreementTypes: AgreementType[], type: AgreementTypeEnum | null | undefined, charterType: CharterType | null | undefined) => {
	const agreementType = agreementTypes?.find(x => x?.type === type);
	const invoiceGroupType = charterType === CharterType.Sales ? agreementType?.invoiceGroupTypeSales : agreementType?.invoiceGroupTypePurchase;
	return charterType && invoiceGroupType?.charterTypes?.includes(charterType) ? invoiceGroupType : undefined;
};

export const getAgreementTypeComparator = (favorites: Favorite[], agreementTypes: AgreementType[]) => {
	const favoriteAgreementTypes = getFavoriteAgreementTypes(favorites, agreementTypes);
	const orderedAgreementTypes = getAgreementTypeOrder(agreementTypes);

	const sortByFavorite = (type?: AgreementTypeEnum) => (type && favoriteAgreementTypes.includes(type) ? 0 : 1);
	const sortByOrder = (type?: AgreementTypeEnum) => type ? (orderedAgreementTypes.get(type) ?? Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;

	return (a: AgreementTypeEnum, b: AgreementTypeEnum) => {
		const favorite = sortByFavorite(a) - sortByFavorite(b);
		if (favorite !== 0) {
			return favorite;
		}

		const order = sortByOrder(a) - sortByOrder(b);
		if (order !== 0) {
			return order;
		}

		return a.localeCompare(b);
	};
};

export const buildAgreementOptions = (
	{
		compatibleAgreements,
		agreementTypes,
		favorites,
		customAgreementTypes,
	}: {
		compatibleAgreements: AgreementTypeEnum[];
		agreementTypes?: AgreementType[];
		favorites: Favorite[];
		customAgreementTypes?: AgreementTypeEnum[];
	}) => {
	const types = agreementTypes ?? [];
	const customTypes = getCustomAgreementTypes(customAgreementTypes);

	const filteredAgreements = customTypes ? compatibleAgreements.filter(t => customTypes.includes(t)) : compatibleAgreements;

	return filteredAgreements.toSorted(getAgreementTypeComparator(favorites, types));
};

export const sortAgreements = <A extends SortableAgreement,>(favorites: Favorite[], agreementTypes: AgreementType[], agreements: A[]): A[] => {
	const favoriteAgreementTypes = getFavoriteAgreementTypes(favorites, agreementTypes);
	const orderedAgreementTypes = getAgreementTypeOrder(agreementTypes);

	const sortByFavorite = (type?: AgreementTypeEnum) => (type && favoriteAgreementTypes.includes(type) ? 0 : 1);
	const sortByOrder = (type?: AgreementTypeEnum) => type
		? (orderedAgreementTypes.get(type) ?? Number.POSITIVE_INFINITY)
		: Number.POSITIVE_INFINITY;

	const groups = new Map<AgreementTypeEnum | "UNKNOWN", A[]>();
	for (const a of agreements) {
		const k = (a.type ?? "UNKNOWN") as AgreementTypeEnum | "UNKNOWN";
		const arr = groups.get(k);
		if (arr) {
			arr.push(a);
		} else {
			groups.set(k, [ a ]);
		}
	}

	const sortedGroups = [ ...groups.keys() ].toSorted((a, b) => {
		const firstType = a !== "UNKNOWN" ? a : undefined;
		const secondType = b !== "UNKNOWN" ? b : undefined;

		const favoriteComparison = sortByFavorite(firstType) - sortByFavorite(secondType);
		if (favoriteComparison !== 0) {
			return favoriteComparison;
		}

		const orderComparison = sortByOrder(firstType) - sortByOrder(secondType);
		if (orderComparison !== 0) {
			return orderComparison;
		}

		return String(firstType ?? "").localeCompare(String(secondType ?? ""));
	});

	const sortedAgreements: A[] = [];
	for (const key of sortedGroups) {
		const items = groups.get(key) ?? [];

		const sortedItems = [ ...items ].toSorted((a, b) => {
			const priorityComparison = (a.priority ?? Number.POSITIVE_INFINITY) - (b.priority ?? Number.POSITIVE_INFINITY);
			if (priorityComparison !== 0) {
				return priorityComparison;
			}

			return (a.type ?? "").localeCompare((b.type ?? ""));
		});

		sortedAgreements.push(...sortedItems);
	}

	return sortedAgreements;
};

export const hasSameAgreementOrder = (a: AgreementFormInput[], b: AgreementFormInput[]) => {
	if (a.length !== b.length) {
		return false;
	}

	const key = (item: AgreementFormInput) => (item.id != null ? `id:${item.id}` : `uuid:${item.uuid ?? ""}`);

	for (let i = 0; i < a.length; i++) {
		if (key(a[i]) !== key(b[i])) {
			return false;
		}
	}

	return true;
};

export const filterServiceAgreements = (
	types?: AgreementTypeEnum[],
	charterType?: CharterType,
	charterSubtype?: CharterSubtype,
	cargos?: NonNullable<CargoLinkedCharter["cargos"]>,
) => {
	if (!types || types.length === 0) {
		return undefined;
	}

	if (!charterType || !charterSubtype) {
		return undefined;
	}

	if (!cargos || cargos.length === 0) {
		return types;
	}

	const predicates: Partial<Record<AgreementTypeEnum, boolean>> = {
		[AgreementTypeEnum.Cleaning]: cargos.some(cargo => cargo.isCleaningRequired),
		[AgreementTypeEnum.NitrogenPurging]: cargos.some(cargo => cargo.isNitrogenPurgingRequired),
		[AgreementTypeEnum.SupplementaryHeating]: cargos.some(cargo => cargo.isTemperatureControlRequired),
		[AgreementTypeEnum.HoseRental]: cargos.some(cargo => cargo.isHoseRentalRequired),
		[AgreementTypeEnum.Ship]: charterType === CharterType.Sales && charterSubtype === CharterSubtype.Time,
	};

	const filteredAgreementTypes = types.filter(type => predicates[type] ?? true);
	return filteredAgreementTypes.length > 0 ? filteredAgreementTypes : undefined;
};

export const getAgreementCardColor = (agreement: Agreement) => {
	if (agreement.isDefault) {
		return "red";
	}
};

export const getAgreementCardTooltip = (agreement: Pick<Agreement, "isDefault" | "manualActionRequired">) => {
	if (agreement.isDefault) {
		return <Tooltip
			sx={{ marginRight: 1 }}
			title={intl.formatMessage({ id: "DEFAULT_AGREEMENT_MISSING_DATA" })}>
			<InfoOutlined color="error" fontSize="small" />
		</Tooltip>;
	} else {
		return null;
	}
};

export const getAgreementCardTitle = (agreement: Pick<Agreement, "type" | "summary" | "isDefault" | "manualActionRequired"> & {
	calculationMethod?: Pick<CalculationMethod, "summary"> | null
}) => {
	return <Box sx={{ display: "flex", alignItems: "center" }}>
		{getAgreementCardTooltip(agreement)}
		<span style={{ minWidth: 150 }}>
			<Typography
				textTransform="none"
				component="span"
				fontSize={13}
				fontWeight="bold">
				{intl.formatMessage({ id: `AGREEMENT_TYPE_${agreement.type}` })}
			</Typography>
		</span>
		<Typography
			textTransform="none"
			component="span"
			fontSize={13}>
			{agreement.calculationMethod?.summary || agreement.summary}
		</Typography>
	</Box>;
};

export const getAgreementCardSubtitle = (agreement: Pick<Agreement, "conditionsSummary">) => {
	return agreement.conditionsSummary && (
		<Typography
			textTransform="none"
			component="span"
			fontSize={13}
			fontStyle={"italic"}
		><span style={{ marginLeft: 150 }}>{agreement.conditionsSummary}</span>
		</Typography>
	);
};

export const getAgreementCardActions = (agreement: Pick<Agreement, "id" | "type">, voyageId?: number): CardAction[] => {
	if (agreement.type !== AgreementTypeEnum.Pjk || voyageId == null) {
		return [];
	}

	return [ {
		render: () => <PjkRateAction agreementId={agreement.id} voyageId={voyageId} />,
	} ];
};
